import React, { useState, useEffect, useMemo } from 'react'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'

import _ from 'lodash'
import useLocalStorage from '@rehooks/local-storage'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { BrowserRouter as Router } from 'react-router-dom'
import { Provider, useDispatch } from 'react-redux'
import axios from 'axios'
import curlirize from 'axios-curlirize'
const qs = require('qs')

import { theme } from './utils/theme'
import RoutePage from './Route'
import QueueWaiting from 'src/components/QueueWaiting'
import PageLoading from './components/PageLoading'
import { setupStore } from './redux/config/configStore'
import { learnerCheckIn, profile, prospectProcessResult } from './utils/apiPath'
import {
  openErrorWithCurlDialog,
  openDialog,
  openErrorDialog,
} from './redux/slices/alertDialog'
import AlertDialog from './components/AlertDialog'
import Error401 from './components/AlertDialog/Error401'
import { AuthProvider, useAuth } from 'react-oidc-context'
import keycloakConfig from './keycloak'
import ErrorExpire from './components/AlertDialog/ErrorExpire'
import { normalizeUser, parseJwt } from './utils/lib'
import { jwtIgnore } from './constants/path'
import { handleDmsExceptPath, handleExceptPath } from './events'
import OneAccount from './OneAccount'
import AgentOneAccount from './AgentOneAccount'
import {
  ensureFileTicket,
  installTicketedImageRetry,
} from './utils/fileTicket'
import {
  AGENT_SSO,
  SSO_SUB_KEY,
  handleAgentSsoError,
  handleAgentSsoUser,
  resolveAgentSsoAction,
  startAgentLogin,
  startAgentSsoCheck,
} from './utils/agentSsoSync'

export const store = setupStore()

const agentSsoAction = resolveAgentSsoAction()

export const instanceResfresh = axios.create({
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
  },
})

export const instanceKeycloak = axios.create({
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
  },
  timeout: 60000,
})

export const instanceOneAccount = axios.create({
  baseURL: `${OneAccount.authority}`,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
  },
  timeout: 10000,
})

export const instanceAgentOneAccount = axios.create({
  baseURL: `${window.__env__.REACT_APP_API_MIDDLEWARE_URL}`,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
  },
  timeout: 10000,
})

const Root = () => {
  const [type, setType] = useLocalStorage('type')
  const searchParams = new URLSearchParams(window.location.search)
  const iproForceLogin = searchParams.get('iPro') === 'true'

  useEffect(() => {
    if (iproForceLogin) {
      console.log('✅ iPro force login detected')
      localStorage.setItem('previousPath', '/dms')
      setType('Agent')
      window.location = '/logging-in' // 👉 ไปยังหน้ารอตรวจสอบ login
    }
  }, [iproForceLogin])

  const oidcConfig = useMemo(() => {
    if (type === 'Staff' && window.__env__.ENV !== 'DEV') {
      return OneAccount
    } else if (type === 'Agent' && window.__env__.ENV !== 'DEV') {
      return AgentOneAccount
    }
    return keycloakConfig(type)
  }, [type])

  return (
    <AuthProvider {...oidcConfig}>
      <Provider store={store}>
        <App />
      </Provider>
    </AuthProvider>
  )
}

export default Root

export const App = () => {
  const auth = useAuth()
  const dispatch = useDispatch()

  const user = JSON.parse(localStorage.getItem('user'))
  const [previousPath] = useLocalStorage('previousPath')
  const [, setType] = useLocalStorage('type')
  const [isLoading, setLoading] = useState(false)
  const [IsStatus, setStatus] = useState(0)
  const [hasVerified, setHasVerified] = useState(false)
  const [agentSso, setAgentSso] = useState(agentSsoAction)

  const refreshToken = async () => {
    try {
      const userType = localStorage.getItem('type')
      const refreshToken = localStorage.getItem('refresh_token')
      const body = {
        refresh_token: refreshToken,
        type: userType,
      }
      const isNotKeyCloak =
        ['Staff', 'Agent'].includes(userType) && window.__env__.ENV !== 'DEV'
      instanceResfresh.defaults.baseURL =
        window.__env__.REACT_APP_API_MIDDLEWARE_URL
      const getToken = await instanceResfresh({
        method: 'post',
        url: '/api/auth/refresh',
        data: qs.stringify(body),
        timeout: isNotKeyCloak ? 10000 : 60000,
      })
      const { data } = getToken

      if (!_.isEmpty(data?.refresh_token)) {
        localStorage.setItem('refresh_token', data.refresh_token)
      }
      const token =
        userType === 'Staff'
          ? data?.access_token || data?.id_token || data?.accessToken
          : data?.id_token || data?.accessToken || data?.access_token
      localStorage.setItem('token', `${data.token_type} ${token}`)
      void ensureFileTicket({ force: true })
      return `${data.token_type} ${token}`
    } catch (error) {
      console.log('error ->', error)
      return 'expire'
    }
  }

  axios.interceptors.request.use(
    async (config) => {
      config.baseURL = window.__env__.REACT_APP_API_URL
      config.withCredentials = true
      const ignore = !_.isEmpty(jwtIgnore.find((el) => el === config.url))
      const accessToken = auth.user?.access_token
      const token = localStorage.getItem('token')
      if (!_.isEmpty(token) && !ignore) {
        config.headers.Authorization = `${token}`
      } else if (accessToken && !ignore) {
        config.headers.Authorization = `${auth.user.token_type} ${accessToken}`
      } else if (_.isEmpty(token)) {
        config.headers.Authorization = 'NoToken'
      }
      config.timeout = 60000
      return config
    },
    function (err) {
      return Promise.reject(err)
    },
  )

  axios.interceptors.response.use(
    async (response) => {
      const getToken = localStorage.getItem('token')

      const token = getToken?.replace('Bearer ', '')
      const decodedJwt = parseJwt(token)

      if (decodedJwt && token) {
        const expire = 60000 * 10
        const expirationTime = decodedJwt?.exp * 1000 - expire
        const dateNow = new Date()
        if (dateNow >= expirationTime) {
          await refreshToken()
        }
      }
      return response
    },
    async (error) => {
      const status = _.get(error, 'response.status', 500)
      const url = _.get(error, 'config.url', '')

      const config = error?.config
      if (status === 401 && !config?.sent) {
        config.sent = true
        const newToken = await refreshToken()
        config.expire = newToken
        if (newToken !== 'expire') {
          config.headers.Authorization = newToken
        }
        config.headers = { ...config.headers }
        return axios(config)
      }

      if (status === 401 && config?.expire === 'expire') {
        dispatch(
          openDialog({
            type: 'custom',
            content: <ErrorExpire />,
            isCloseDialog: false,
            zIndex: 5000,
          }),
        )
        return Promise.reject(error)
      }

      if (status === 401) {
        dispatch(
          openDialog({
            type: 'custom',
            content: <Error401 />,
            isCloseDialog: false,
          }),
        )
        return Promise.reject(error)
      }
      console.log('learnerCheckIn ->', learnerCheckIn)
      console.log('url ->', url)
      if (url === learnerCheckIn) return Promise.reject(error)
      if ((status >= 500 && status < 600) || status === 0) {
        if (axios.isCancel(error)) {
          console.log('Request was canceled:', error.message)
          return Promise.reject(error)
        }
        dispatch(openErrorWithCurlDialog(error))
        return Promise.reject(error)
      }
      const body = _.get(error, 'response.data.constraints', null)
      console.log('body ->', body)
      if (
        !_.isEmpty(body) &&
        !body.isCustom &&
        !['/e-learning-log-report/update'].includes(url)
      ) {
        dispatch(openErrorDialog({ ...body, message: body?.value }))
        return Promise.reject(error)
      }

      return Promise.reject(error)
    },
  )

  curlirize(axios, (result, err) => {
    if (err) {
      console.error(err)
    }
  })

  const handleGetProfileSuccess = (res) => {
    const userData = _.get(res, 'data', null)
    const isProspect = _.get(userData, 'isProspect', false)
    const normalizedUser = normalizeUser(userData)
    localStorage.setItem('user', JSON.stringify(normalizedUser))
    setLoading(false)
    if (isProspect) {
      dispatch(
        openDialog({
          isCloseDialog: false,
          iconType: 'warning',
          title: 'รวมข้อมูลประวัติผู้ใช้งาน',
          message: [
            'ระบบจะเปลี่ยนบัญชีผู้ใช้งานให้กับคุณ',
            'และรวมข้อมูลส่วนตัวพร้อมประวัติการเรียนให้คุณอัตโนมัติ',
          ],
          buttonRight: { label: 'รับทราบ' },
        }),
      )
    }

    if (userData?.userType === 'STAFF') {
      const defaultStaffPath = '/dms/staff/home'
      window.location = defaultStaffPath
    }
  }

  const handleGetProfileError = (err) => {
    setLoading(false)
    const errStatus = _.get(err, 'response.status', 500)
    window.localStorage.setItem('log_error', errStatus)
    setStatus(errStatus)
    if (errStatus !== 0) {
      window.location.href = '/login'
    }
  }

  useEffect(() => {
    if (agentSso?.type === AGENT_SSO.CHECK) {
      startAgentSsoCheck(auth, agentSso)
      return
    }
    if (agentSso?.type === AGENT_SSO.LOGIN) {
      startAgentLogin(agentSso)
      return
    }
    installTicketedImageRetry()
    void ensureFileTicket()
  }, [])

  useEffect(() => {
    if (auth.error && agentSso?.type === AGENT_SSO.CALLBACK) {
      handleAgentSsoError(agentSso)
    }
  }, [auth.error])

  useEffect(() => {
    return auth.events.addUserLoaded((authorization) => {
      if (agentSso?.type === AGENT_SSO.CALLBACK) {
        if (handleAgentSsoUser(authorization, agentSso)) setAgentSso(null)
        return
      }
      if (!_.isEmpty(authorization?.access_token) && _.isEmpty(user)) {
        axios.defaults.baseURL = window.__env__.REACT_APP_API_URL
        axios.defaults.headers = {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-store',
          Pragma: 'no-cache',
          Expires: '0',
          Authorization: `${authorization.token_type} ${authorization?.access_token}`,
        }
        setLoading(true)
        const type = localStorage.getItem('type')
        localStorage.setItem('refresh_token', authorization.refresh_token)
        localStorage.setItem(SSO_SUB_KEY, authorization.profile?.sub || '')
        localStorage.setItem(
          'token',
          `${authorization.token_type} ${authorization?.access_token}`,
        )
        if (type === 'Agent' && window.__env__.ENV !== 'DEV') {
          localStorage.setItem(
            'token',
            `${authorization.token_type} ${authorization?.id_token}`,
          )
        }

        void ensureFileTicket({ force: true })

        if (type === 'Agent') {
          window.location = '/waiting-for-login'
        } else {
          axios
            .get(profile, { params: { login: 'true' } })
            .then((res) => handleGetProfileSuccess(res))
            .catch((err) => handleGetProfileError(err))
        }
      }
    })
  }, [auth.events, user, agentSso])

  useEffect(() => {
    const uuid = _.get(user, 'uuid', '')
    const isDMS = _.get(user, 'prospectProfile.isDMS', false)
    const isVerified = _.get(user, 'isVerifiedByiRecruit', false)

    if (uuid && isDMS && !isVerified && !hasVerified) {
      setHasVerified(true)
      setLoading(true)
      axios
        .post(`${prospectProcessResult}`, { uuid: uuid })
        .then((res) => {
          const verifiedUser = res.data
          const oldUser = JSON.parse(localStorage.getItem('user') || '{}')
          const newUser = {
            ...oldUser,
            approvalStatus: verifiedUser.approvalStatus,
            isVerifiedByiRecruit: verifiedUser.isVerifiedByiRecruit,
          }
          localStorage.setItem('user', JSON.stringify(newUser))
          setLoading(false)
        })
        .catch((err) => {
          console.error('❌ Failed to verify DMS user:', err)
          setLoading(false)
        })
    }
  }, [user, hasVerified])

  useEffect(() => {
    let logerror = window.localStorage.getItem('log_error')
    if (IsStatus === 401 || logerror === '401') {
      dispatch(
        openDialog({
          type: 'custom',
          content: <Error401 />,
          isCloseDialog: false,
        }),
      )
    }
  }, [IsStatus])

  const onFocus = () => {
    if (
      _.isEmpty(window.localStorage.getItem('user')) &&
      !handleExceptPath(window.location)
    ) {
      window.location.href = '/login'
    }
  }

  useEffect(() => {
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
    }
  }, [user, window?.location?.pathname])

  const handleSwitchFromBackend = () => {
    if (_.isNull(user)) {
      setType('Staff')
      window.localStorage.setItem('previousPath', '/my-profile')
      window.location = '/logging-in'
    } else {
      window.location = '/my-profile'
    }
  }

  useEffect(() => {
    if (window?.location?.pathname !== '/switch-from-backend') return
    handleSwitchFromBackend()
  }, [user, window?.location?.pathname])

  const isDms =
    previousPath?.includes('/dms/') &&
    !handleDmsExceptPath(window?.location) &&
    _.isEmpty(user)

  return (
    <ThemeProvider theme={theme}>
      <PageLoading />
      <QueueWaiting />
      <AlertDialog />
      <CssBaseline />
      {isLoading || isDms || agentSso ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            backgroundColor: '#FFFFFF',
            zIndex: '99999',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={30} />
          <Typography sx={{ mt: 2 }}>กรุณารอสักครู่</Typography>
        </Box>
      ) : (
        <AppRoute user={user} />
      )}
    </ThemeProvider>
  )
}

export const AppRoute = ({ user }) => {
  const auth = useAuth()
  if (auth.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          backgroundColor: '#FFFFFF',
          zIndex: '99999',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={30} />
        <Typography sx={{ mt: 2 }}>กรุณารอสักครู่</Typography>
      </Box>
    )
  }

  return (
    <Router>
      <RoutePage user={user} />
    </Router>
  )
}
